import { defineComponent as _defineComponent } from 'vue'
import { toRefs, ref, watch, computed, Ref } from 'vue';
import { MaterialV1, VideoV1 } from '@ilteducation/content-registry-types';
import type { Area, Category } from '@ilteducation/content-ref-data-types';
import { getAllRefData } from '@/data-provider/refDataProvider';
import customProvider from '@/data-provider/customProvider';
import { areasOfCategories, categoryColorLookup, filterCategories } from '@/foundations/helpers/refDataFilters';
import RefDataAutocomplete from '@/components/material/RefDataAutocomplete.vue';
import NoRelatedVideoWarning from '@/components/material/NoRelatedVideoWarning.vue';
import SuggestionChips from '@/components/material/SuggestionChips.vue';

type Emits = {
  (e: 'update:categories', categories: MaterialV1['categories']): void;
  (e: 'update:areas', areas: MaterialV1['areas']): void;
};
type Props = {
  areas: MaterialV1['areas'];
  categories: MaterialV1['categories'];
  contentLinks: MaterialV1['contentLinks'];
  /** Markets to narrow the pick lists to — the union across the rows being edited. */
  realms?: string[];
  subscriptionTypes?: string[];
  /** Grades and Areas are manabua-only. Other products tag with plain categories alone. */
  showGradesAndAreas?: boolean;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'ContentRefDataTags',
  props: {
    areas: null,
    categories: null,
    contentLinks: null,
    realms: null,
    subscriptionTypes: null,
    showGradesAndAreas: { type: Boolean }
  },
  emits: ["update:categories", "update:areas"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update:categories', categories: MaterialV1['categories']): void;
  (e: 'update:areas', areas: MaterialV1['areas']): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  areas: MaterialV1['areas'];
  categories: MaterialV1['categories'];
  contentLinks: MaterialV1['contentLinks'];
  /** Markets to narrow the pick lists to — the union across the rows being edited. */
  realms?: string[];
  subscriptionTypes?: string[];
  /** Grades and Areas are manabua-only. Other products tag with plain categories alone. */
  showGradesAndAreas?: boolean;
}



const availableCategories = ref<Category[]>([]);
getAllRefData('categories').then((categories) => {
  availableCategories.value = categories;
});
const availableAreas = ref<Area[]>([]);
getAllRefData('areas').then((areas) => {
  availableAreas.value = areas;
});


const { areas } = toRefs(props);
const relatedVideos = ref<VideoV1[]>([]);

const areasModel = ref(areas.value ?? []);
// Split by kind so the Grades field holds grades. The material persists one merged
// `categories` array, so the two models are recombined on emit.
const subjectsModel = ref<string[]>([]);
const gradesModel = ref<string[]>([]);
const categoryColor = computed(() => categoryColorLookup(availableCategories.value));

const scope = computed(() => ({ realms: props.realms, subscriptionTypes: props.subscriptionTypes }));
// selectedIds keeps whatever is already on the material visible even if it no longer
// qualifies, so opening the form can't quietly discard it.
// Grades have their own field, so Categories is everything else. Matching on kind === 'subject'
// instead would hide every category whose kind is 'shortcut' or unset — which is most of the
// non-manabua ones, and on a material carrying both product families it dropped them silently.
const offeredSubjects = computed(() =>
  filterCategories(availableCategories.value, {
    ...scope.value,
    excludeKinds: ['grade'],
    selectedIds: subjectsModel.value,
  }),
);
const offeredGrades = computed(() =>
  filterCategories(availableCategories.value, { ...scope.value, kind: 'grade', selectedIds: gradesModel.value }),
);
// Areas inherit realm/subscription from their parent category.
const offeredAreas = computed(() =>
  areasOfCategories(availableAreas.value, filterCategories(availableCategories.value, scope.value), areasModel.value),
);

const videoAreas = computed(() => {
  return [...new Set(relatedVideos.value.map((video) => video.orderedAreas.map((area) => area.id)).flat())];
});
const suggestedAreas = computed(() =>
  availableAreas.value.filter((area) => !areasModel.value.includes(area.id) && videoAreas.value.includes(area.id)),
);

const videoCategories = computed(() => [
  ...new Set(relatedVideos.value.map((video) => video.categories.map((category) => category)).flat()),
]);

const suggestedCategories = computed(() =>
  availableCategories.value.filter(
    (category) =>
      ![...subjectsModel.value, ...gradesModel.value].includes(category.id) &&
      videoCategories.value.includes(category.id),
  ),
);
const suggestedSubjects = computed(() => suggestedCategories.value.filter((category) => category.kind !== 'grade'));
const suggestedGrades = computed(() => suggestedCategories.value.filter((category) => category.kind === 'grade'));

watch(
  () => props.areas,
  (newAreas) => {
    areasModel.value = newAreas ?? [];
  },
);
watch(
  () => [props.categories, availableCategories.value],
  ([materialCategories, categories]) => {
    if (!materialCategories || !categories) {
      return;
    }
    const selected = (categories as Category[]).filter((category) => materialCategories.includes(category.id));
    subjectsModel.value = selected.filter((category) => category.kind !== 'grade').map((c) => c.id);
    // Grades stay split out even when the field is hidden, so a value already on the material
    // survives the round trip instead of being dropped by the next emit.
    gradesModel.value = selected.filter((category) => category.kind === 'grade').map((c) => c.id);
  },
  { immediate: true },
);
watch(
  () => props.contentLinks,
  (newContentLinks) => {
    const videoIds =
      newContentLinks
        ?.filter((contentLink) => contentLink.contentType === 'Video')
        .map((contentLink) => contentLink.id) ?? [];
    if (!videoIds.length) {
      relatedVideos.value = [];
      return;
    }
    customProvider()
      .getList('videos', {
        filter: { id: videoIds },
        pagination: { page: 1, limit: 1000 },
        sort: [],
      })
      .then((response) => {
        relatedVideos.value = response.data as VideoV1[];
      });
  },
  { immediate: true },
);
// Watch the ids, not the lengths: swapping one category for another leaves the count untouched
// and the edit never reached the parent, so saving looked like it did nothing. The guards below
// already make a redundant firing free.
watch(
  () => [...subjectsModel.value, ...gradesModel.value],
  () => {
    const newCategories = [...subjectsModel.value, ...gradesModel.value];
    if (
      props.categories?.length === newCategories.length &&
      props.categories?.every((c) => newCategories.includes(c))
    ) {
      // unchanged
      return;
    }
    emit('update:categories', newCategories);
  },
);
watch(
  () => [...areasModel.value],
  () => {
    if (props.areas?.length === areasModel.value.length && props.areas?.every((a) => areasModel.value.includes(a))) {
      // unchanged
      return;
    }
    emit('update:areas', areasModel.value);
  },
);

return { __sfc: true,emit, availableCategories, availableAreas, props, areas, relatedVideos, areasModel, subjectsModel, gradesModel, categoryColor, scope, offeredSubjects, offeredGrades, offeredAreas, videoAreas, suggestedAreas, videoCategories, suggestedCategories, suggestedSubjects, suggestedGrades, RefDataAutocomplete, NoRelatedVideoWarning, SuggestionChips }
}

})